import { Reveal, Chapter, SectionHead } from "@/components/Reveal";
import { ValidatorCta } from "@/components/Buttons";
import { WorldMapScene } from "@/components/WorldMapScene";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Server, ScanSearch, TrendingUp, Sparkles } from "lucide-react";

const POINTS = [
  { icon: Server, title: "Run infrastructure", text: "Operate verifier nodes and maintain availability." },
  { icon: ScanSearch, title: "Verify observations", text: "Attest to the quality and authenticity of network data." },
  { icon: TrendingUp, title: "Build reputation", text: "Reliable verification builds your standing in the network." },
  { icon: Sparkles, title: "Early ecosystem access", text: "Founding Verifiers are first in line as the network opens." },
];

const DEEP_DIVE = [
  {
    q: "What does a Founding Verifier actually do?",
    a: "Verifiers operate Synaphy verifier node infrastructure and maintain network uptime and reliability. They participate in selected data verification and attestation tasks, help identify duplicate, suspicious, manipulated or low-quality submissions, take part in consensus or other verification mechanisms defined by the protocol, and maintain verifier security while following network rules.",
  },
  {
    q: "Do Verifiers work manually 24/7?",
    a: "No. The verifier node is designed to operate continuously and automatically. The operator monitors infrastructure, maintains availability and responds to alerts when required. Verifiers do not need to manually work around the clock.",
  },
  {
    q: "How does a Verifier verify a Contributor?",
    a: "Verifiers never manually inspect every submission. AI performs the first layer of checks using signals such as location, timestamp, image quality, device signals, duplicate detection and suspicious behaviour. Selected or high-risk submissions are then reviewed by one or more verifiers, and agreement between independent verifiers increases confidence in the data.",
  },
  {
    q: "How do Verifier rewards work?",
    a: "Verifier rewards are designed to come from real network economics: verified physical-world datasets, API access, enterprise services, network services or marketplace / protocol fees. A defined portion of eligible protocol revenue would be allocated to verifiers according to the final reward mechanism. Rewards, if enabled, are determined by final protocol rules, network economics and verified performance.",
  },
  {
    q: "Are the earning scenarios a promise?",
    a: "No. Any earning scenarios in the Founding Verifier documentation are illustrative examples only. They are not income promises. Synaphy does not offer fixed returns, fixed daily income, guaranteed monthly payouts or guaranteed earnings of any kind. The actual verifier pool, percentages and distribution formula are defined by the final protocol.",
  },
  {
    q: "What about Verifier operating costs?",
    a: "Running infrastructure can involve cloud/server, bandwidth, storage, monitoring, backup and security costs. Gross verifier rewards and actual net operating surplus are not the same thing, and candidates should evaluate both during due diligence.",
  },
  {
    q: "Why only 100 Founding Verifiers?",
    a: "A limited Founding group makes it easier to test the network, establish a reliable verification layer and build an initial operator community. The strategy is simple: first strong verifiers, then pilot proof, then network growth, then scale.",
  },
  {
    q: "What are the participation terms?",
    a: "Founding Verifier participation includes a defined infrastructure commitment tied to a genuine participation and infrastructure program. Before any participation is accepted, Synaphy defines and shares the legal structure, use of funds, lock period, exit rules, penalty conditions, operating requirements and the reward formula during the application and due-diligence process.",
  },
];

export const Validators = () => {
  return (
    <section id="validators" className="relative py-28 sm:py-40 bg-ink-900/40" data-testid="section-validators">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Chapter label="Founding Verifiers" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <SectionHead title={"Help secure the physical\nworld data network."} />
          </div>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="text-lg leading-relaxed text-steel max-w-md">
              Founding Verifiers verify network observations, maintain network
              integrity and form the early infrastructure layer. The Founding
              cohort is limited to 100 positions.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="relative mt-16 h-[300px] sm:h-[420px]" data-testid="validator-globe">
            <WorldMapScene labels converge={false} className="absolute inset-0" />
            <div className="absolute bottom-0 left-0 font-mono text-[9px] tracking-[0.18em] text-zinc-600 uppercase">
              Illustrative network locations · confirmed during onboarding
            </div>
          </div>
        </Reveal>

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10">
          {POINTS.map((p, i) => (
            <Reveal key={p.title} delay={0.08 * i} y={14}>
              <div className="py-7 border-t border-white/5" data-testid={`validator-point-${i}`}>
                <p.icon className="h-6 w-6 text-signal-violet mb-5" />
                <h3 className="font-display text-lg font-semibold tracking-tight text-mist">{p.title}</h3>
                <p className="mt-1.5 text-[15px] leading-relaxed text-steel">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.25}>
          <div className="mt-12">
            <ValidatorCta testid="validators-apply-btn">Apply to become a Founding Verifier</ValidatorCta>
          </div>
        </Reveal>

        <div className="mt-24 max-w-3xl">
          <Reveal>
            <h3 className="font-display text-2xl font-semibold tracking-tight text-mist">
              Verifier deep-dive
            </h3>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-8" data-testid="validator-deep-dive">
              {DEEP_DIVE.map((d, i) => (
                <AccordionItem key={d.q} value={`vd-${i}`} className="border-b border-white/5" data-testid={`validator-faq-${i}`}>
                  <AccordionTrigger className="py-5 text-left text-base font-medium text-mist hover:text-signal-cyan hover:no-underline transition-colors [&[data-state=open]]:text-signal-cyan">
                    {d.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-[15px] leading-relaxed text-steel max-w-2xl">
                    {d.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
